import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../utils/axiosInstance";
import {
  saveToCacheDB,
  getFromCacheDB,
  clearCacheDB,
} from "../utils/indexedDBCache";
import {
  importExistingDonors,
  updateDonor,
  updateDonorLocally,
} from "./donorSlice";

// Fetch all donations (with authentication)
export const fetchDonation = createAsyncThunk(
  "donation/fetchDonations",
  async (queryParams = {}, { getState }) => {
    const { auth } = getState();
    const cacheKey = "all_donations";

    // Try cache first (only if no query params)
    if (!queryParams || Object.keys(queryParams).length === 0) {
      const cachedData = await getFromCacheDB(cacheKey, 5 * 60 * 1000); // 5 min cache
      if (cachedData) {
        return cachedData;
      }
    }

    const startTime = Date.now();

    const response = await axiosInstance.get("/api/donations", {
      params: queryParams,
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
      timeout: 60000,
    });

    const duration = Date.now() - startTime;

    // Cache only if no query params
    if (!queryParams || Object.keys(queryParams).length === 0) {
      await saveToCacheDB(cacheKey, response.data);
    }

    return response.data;
  },
);

export const fetchConsolidatedDonations = createAsyncThunk(
  "donation/fetchConsolidatedDonations",
  async (forceRefresh = false, { getState }) => {
    try {
      const { auth } = getState();
      const cacheKey = "consolidated_donations";

      // Try to get from IndexedDB cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedData = await getFromCacheDB(cacheKey, 5 * 60 * 1000); // 5 minutes cache
        if (cachedData) {
          return cachedData;
        }
      }

      // Fetch from API
      const startTime = Date.now();

      const response = await axiosInstance.get("/api/donations/consolidated", {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
        timeout: 60000, // 60 seconds timeout for large dataset
      });

      const duration = Date.now() - startTime;

      // Save to IndexedDB cache for next time
      await saveToCacheDB(cacheKey, response.data);

      return response.data;
    } catch (error) {
      console.error("Error fetching consolidated donations:", error);
      throw error;
    }
  },
);

export const transactionId = createAsyncThunk(
  "donation/transactionId",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axiosInstance.get("api/donations/transactionId", {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });
      return Number(response.data.transactionId);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch last donor ID",
      );
    }
  },
);

// Create a new donation (authentication added)
export const createDonation = createAsyncThunk(
  "donation/createDonation",
  async (donationData, { getState, rejectWithValue }) => {
    try {
      const { auth, donation } = getState();
      const transactionId = Number(donation.transactionId);
      const finalDonationData = {
        ...donationData,
        transactionId,
      };
      const response = await axiosInstance.post(
        "/api/donations",
        finalDonationData,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Unknown error occurred");
    }
  },
);

// Update a donation
export const updateDonation = createAsyncThunk(
  "donation/updateDonation",
  async (donationData, { dispatch, getState }) => {
    const { auth } = getState();
    const { _id } = donationData;

    const response = await axiosInstance.put(
      `/api/donations/${_id}`,
      donationData,
      {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      },
    );

    const { updatedDonation, updatedDonor } = response.data;
    if (updatedDonor) {
      dispatch(updateDonorLocally(updatedDonor));
    }
    return updatedDonation;
  },
);

// Delete a donation
export const deleteDonation = createAsyncThunk(
  "donation/deleteDonation",
  async (id, { getState }) => {
    const { auth } = getState();
    await axiosInstance.delete(`/api/donations/${id}`, {
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
    });
    return id;
  },
);

// Slice
const donationSlice = createSlice({
  name: "donation",
  initialState: {
    donations: [],
    donors: [],
    consolidatedDonations: [],
    loading: false,
    status: "idle",
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateDonorLocally, (state, action) => {
        const updatedDonor = action.payload;

        const donorFields = (({
          firstName,
          lastName,
          phone,
          email,
          address,
        }) => ({
          firstName,
          lastName,
          phone,
          email,
          address,
        }))(updatedDonor);

        // ✅ Update flat donations table
        state.donations = state.donations.map((donation) =>
          donation.donorId === updatedDonor.donorId
            ? { ...donation, ...donorFields }
            : donation,
        );

        // ✅ Update consolidated donations (parent + children)
        state.consolidatedDonations = state.consolidatedDonations.map(
          (consolidated) => ({
            ...consolidated,

            parentDetails:
              consolidated.parentDetails?.donorId === updatedDonor.donorId
                ? { ...consolidated.parentDetails, ...donorFields }
                : consolidated.parentDetails,

            childReceipts: consolidated.childReceipts?.map((child) =>
              child.donorId === updatedDonor.donorId
                ? { ...child, ...donorFields }
                : child,
            ),
          }),
        );
      })
      .addCase(fetchDonation.pending, (state) => {
        state.loading = true;
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchDonation.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded";
        state.donations = action.payload;
      })
      .addCase(fetchDonation.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchConsolidatedDonations.pending, (state) => {
        state.loading = true;
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchConsolidatedDonations.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded";
        state.consolidatedDonations = action.payload;
      })
      .addCase(fetchConsolidatedDonations.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(transactionId.pending, (state) => {
        state.loading = true;
        state.status = "loading";
        state.error = null;
      })
      .addCase(transactionId.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded";
        state.transactionId = action.payload;
      })
      .addCase(transactionId.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(createDonation.pending, (state) => {
        // Clear cache when creating new donation
        clearCacheDB("consolidated_donations");
        clearCacheDB("all_donations");
      })
      .addCase(createDonation.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { donation, paypurpose } = action.payload;

        if (donation) {
          state.donations.push(donation);

          // Increment transactionId for next donation
          if (state.transactionId) {
            state.transactionId = state.transactionId + 1;
          }

          // Update consolidatedDonations
          const receiptNo = donation.receiptNo;
          const existingConsolidated = state.consolidatedDonations.find(
            (c) => c.receiptNo === receiptNo,
          );

          if (existingConsolidated) {
            // Receipt already exists - add as child or update parent
            if (donation.childReceiptNo) {
              // It's a child receipt
              existingConsolidated.childReceipts =
                existingConsolidated.childReceipts || [];
              existingConsolidated.childReceipts.push(donation);
              const newTotalAmount =
                existingConsolidated.totalAmount + Number(donation.amount || 0);
              existingConsolidated.totalAmount = newTotalAmount;
              existingConsolidated.donationCount =
                (existingConsolidated.donationCount || 0) + 1;

              // Update consolidatedAmount in parent details
              if (existingConsolidated.parentDetails) {
                existingConsolidated.parentDetails.consolidatedAmount =
                  newTotalAmount;
              }
            }
          } else {
            // New receipt - create new consolidated entry
            const totalAmount = Number(donation.amount || 0);
            const parentWithConsolidated = {
              ...donation,
              consolidatedAmount: totalAmount,
            };

            // Add to the beginning of the array so it appears at the top
            state.consolidatedDonations.unshift({
              receiptNo: receiptNo,
              totalAmount: totalAmount,
              donationCount: 1,
              parentDetails: parentWithConsolidated,
              childReceipts: [],
            });
          }
        }

        // Note: paypurpose is handled by paypurposeSlice, not here
      })
      .addCase(updateDonation.pending, (state) => {
        // Clear cache when updating donation
        clearCacheDB("consolidated_donations");
        clearCacheDB("all_donations");
      })
      .addCase(updateDonation.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updatedDonation = action.payload;

        if (!updatedDonation) return;

        // Update the one donation in donations array
        const index = state.donations.findIndex(
          (d) => d._id === updatedDonation._id,
        );
        if (index !== -1) state.donations[index] = updatedDonation;

        // Update consolidatedDonations array
        state.consolidatedDonations = state.consolidatedDonations.map(
          (consolidated) => {
            let updated = false;
            let newConsolidated = { ...consolidated };

            // Check if updated donation is the parent
            if (consolidated.parentDetails?._id === updatedDonation._id) {
              newConsolidated.parentDetails = updatedDonation;
              updated = true;
            }

            // Check if updated donation is a child
            if (consolidated.childReceipts) {
              const childIndex = consolidated.childReceipts.findIndex(
                (child) => child._id === updatedDonation._id,
              );
              if (childIndex !== -1) {
                newConsolidated.childReceipts = [...consolidated.childReceipts];
                newConsolidated.childReceipts[childIndex] = updatedDonation;
                updated = true;
              }
            }

            // Recalculate total amount if this consolidated entry was updated
            if (updated) {
              const parentAmount = Number(
                newConsolidated.parentDetails?.amount || 0,
              );
              const childrenAmount = (
                newConsolidated.childReceipts || []
              ).reduce((sum, child) => sum + Number(child.amount || 0), 0);
              const newTotalAmount = parentAmount + childrenAmount;
              newConsolidated.totalAmount = newTotalAmount;

              // Update consolidatedAmount in parent details
              if (newConsolidated.parentDetails) {
                newConsolidated.parentDetails = {
                  ...newConsolidated.parentDetails,
                  consolidatedAmount: newTotalAmount,
                };
              }
            }

            return newConsolidated;
          },
        );
      })
      .addCase(deleteDonation.pending, (state) => {
        // Clear cache when deleting donation
        clearCacheDB("consolidated_donations");
        clearCacheDB("all_donations");
      })
      .addCase(deleteDonation.fulfilled, (state, action) => {
        state.status = "succeeded";
        const deletedId = action.payload;

        // Update donations array
        state.donations = state.donations.filter((d) => d._id !== deletedId);

        // Update consolidatedDonations array
        state.consolidatedDonations = state.consolidatedDonations
          .map((consolidated) => {
            // Check if deleted donation is the parent
            if (consolidated.parentDetails?._id === deletedId) {
              return null; // Remove this consolidated entry
            }

            // Check if deleted donation is a child
            if (consolidated.childReceipts) {
              const updatedChildren = consolidated.childReceipts.filter(
                (child) => child._id !== deletedId,
              );

              // Recalculate total amount if a child was removed
              if (
                updatedChildren.length !== consolidated.childReceipts.length
              ) {
                const parentAmount = Number(
                  consolidated.parentDetails?.amount || 0,
                );
                const newTotal =
                  parentAmount +
                  updatedChildren.reduce(
                    (sum, child) => sum + Number(child.amount || 0),
                    0,
                  );

                return {
                  ...consolidated,
                  childReceipts: updatedChildren,
                  totalAmount: newTotal,
                  parentDetails: {
                    ...consolidated.parentDetails,
                    consolidatedAmount: newTotal,
                  },
                };
              }
            }

            return consolidated;
          })
          .filter(Boolean);
      })
      // Listen to importExistingDonors to update consolidated donations instantly
      .addCase(importExistingDonors.fulfilled, (state, action) => {
        // Clear cache since new data was added
        clearCacheDB("consolidated_donations");
        clearCacheDB("all_donations");

        const { insertedDonations } = action.payload;

        if (insertedDonations && Array.isArray(insertedDonations)) {
          // Process each imported donation
          insertedDonations.forEach((donation) => {
            // Add to donations array
            state.donations.push(donation);

            // Update consolidatedDonations
            const receiptNo = donation.receiptNo;
            const existingConsolidated = state.consolidatedDonations.find(
              (c) => c.receiptNo === receiptNo,
            );

            if (existingConsolidated) {
              // Receipt already exists - add as child or update parent
              if (donation.childReceiptNo) {
                // It's a child receipt
                existingConsolidated.childReceipts =
                  existingConsolidated.childReceipts || [];
                existingConsolidated.childReceipts.push(donation);
                const newTotalAmount =
                  existingConsolidated.totalAmount +
                  Number(donation.amount || 0);
                existingConsolidated.totalAmount = newTotalAmount;
                existingConsolidated.donationCount =
                  (existingConsolidated.donationCount || 0) + 1;

                // Update consolidatedAmount in parent details
                if (existingConsolidated.parentDetails) {
                  existingConsolidated.parentDetails.consolidatedAmount =
                    newTotalAmount;
                }
              }
            } else {
              // New receipt - create new consolidated entry
              const totalAmount = Number(donation.amount || 0);
              const parentWithConsolidated = {
                ...donation,
                consolidatedAmount: totalAmount,
              };

              // Add to the beginning of the array so it appears at the top
              state.consolidatedDonations.unshift({
                receiptNo: receiptNo,
                totalAmount: totalAmount,
                donationCount: 1,
                parentDetails: parentWithConsolidated,
                childReceipts: [],
              });
            }
          });
        }
      })
      .addCase(updateDonor.fulfilled, (state, action) => {
        const updatedDonor = action.payload.updatedDonor || action.payload;

        const donorFields = (({ firstName, lastName, phone, email }) => ({
          firstName,
          lastName,
          phone,
          email,
        }))(updatedDonor);

        // Update donor fields in donations (without overwriting donation._id)
        state.donations = state.donations.map((donation) =>
          donation.donorId === updatedDonor.donorId
            ? { ...donation, ...donorFields }
            : donation,
        );

        // Update consolidatedDonations parent and child
        state.consolidatedDonations = state.consolidatedDonations.map(
          (consolidated) => {
            let updated = { ...consolidated };

            // Update parent
            if (consolidated.parentDetails?.donorId === updatedDonor.donorId) {
              updated.parentDetails = {
                ...consolidated.parentDetails,
                ...donorFields,
              };
            }

            // Update children
            if (consolidated.childReceipts) {
              updated.childReceipts = consolidated.childReceipts.map((child) =>
                child.donorId === updatedDonor.donorId
                  ? { ...child, ...donorFields }
                  : child,
              );
            }

            return updated;
          },
        );
      });
  },
});

export default donationSlice.reducer;
